import { lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Gamepad2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import { TextGenerateEffect } from "@/components/ui/text-generate-effect";
import heroDashboard from "@/assets/hero-dashboard.webp";

// Lazy load below-fold components
const LogoCarousel = lazy(() => import("@/components/LogoCarousel"));
const GamesFilter = lazy(() => import("@/components/GamesFilter"));
const ProductShowcase = lazy(() => import("@/components/ProductShowcase"));
const FeaturesSection = lazy(() => import("@/components/features/FeaturesSection").then(m => ({ default: m.FeaturesSection })));
const PricingSection = lazy(() => import("@/components/pricing/PricingSection").then(m => ({ default: m.PricingSection })));
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection"));
const Footer = lazy(() => import("@/components/Footer"));

const Index = () => {
  return (
    <div className="min-h-screen bg-black text-foreground">
      <Navigation />

      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative container px-4 pt-40 pb-20"
      >
        {/* Background */}
        <div className="absolute inset-0 -z-10 bg-[#0A0A0A]" />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="inline-block mb-4 px-4 py-1.5 rounded-full glass"
        >
          <span className="text-sm font-medium">
            <Gamepad2 className="w-4 h-4 inline-block mr-2" />
            Premium digital marketplace
          </span>
        </motion.div>

        <div className="max-w-4xl relative z-10">
          <h1 className="text-5xl md:text-7xl font-normal mb-4 tracking-tight text-left">
            <span className="text-gray-200">
              <TextGenerateEffect words="Level up gaming" delay={0} showCursor={true} />
            </span>
            <br />
            <span className="text-gray-200">
              <TextGenerateEffect words="with " delay={1.8} showCursor={true} />
            </span>
            <span className="text-white font-bold">
              <TextGenerateEffect words="BlackoutBoost.com" delay={2.1} showCursor={true} />
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 3.8 }}
            className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl text-left"
          >
            Buy verified gaming accounts, in-game items, currencies, and Spotify premium top-ups. Instant delivery,
            secure transactions, and 24/7 support. <span className="text-foreground">Start shopping now.</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 4.3 }}
            className="flex flex-col sm:flex-row gap-4 items-start"
          >
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 4.6 }}>
              <Button size="lg" className="button-gradient">
                Browse Marketplace
              </Button>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 4.9 }}>
              <Button size="lg" variant="link" className="text-white">
                View Deals <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="relative mx-auto max-w-5xl mt-20"
        >
          <div className="glass rounded-xl overflow-hidden">
            <img
              src={heroDashboard}
              alt="BlackoutBoost Gaming Dashboard"
              className="w-full h-auto"
              width="1024"
              height="746"
              fetchPriority="high"
              decoding="async"
            />
          </div>
        </motion.div>
      </motion.section>

      {/* Below-fold content wrapped in Suspense */}
      <Suspense fallback={<div className="h-20" />}>
        {/* Logo Carousel */}
        <LogoCarousel />

        {/* Games Filter */}
        <GamesFilter />

        {/* Product Showcase */}
        <ProductShowcase />

        {/* Features Section */}
        <div id="features" className="bg-black">
          <FeaturesSection />
        </div>

        {/* Pricing Section */}
        <div id="pricing" className="bg-black">
          <PricingSection />
        </div>

        {/* Testimonials Section */}
        <div className="bg-black">
          <TestimonialsSection />
        </div>

        {/* CTA Section */}
        <section className="container px-4 py-20 relative bg-black">
          <div
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage: 'url("/lovable-uploads/21f3edfb-62b5-4e35-9d03-7339d803b980.png")',
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-[#0A0A0A]/80 backdrop-blur-lg border border-white/10 rounded-2xl p-8 md:p-12 text-center relative z-10"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to boost your gaming?</h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join thousands of gamers who have leveled up with BlackoutBoost. Get instant access to premium accounts and
              items.
            </p>
            <Button size="lg" className="button-gradient">
              Get Started
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </motion.div>
        </section>

        {/* Footer */}
        <div className="bg-black">
          <Footer />
        </div>
      </Suspense>
    </div>
  );
};

export default Index;
